import React, { useState, useEffect } from 'react';
import {
    Save,
    User,
    Mail,
    Lock,
    Image,
    Shield,
    Phone,
    Building,
    CheckCircle,
    AlertCircle,
    Eye,
    EyeOff,
    Camera,
    Globe,
    Bell,
    Key,
    RefreshCw,
    LogOut
} from 'lucide-react';
import { userService } from '../../services/userService';
import { authService } from '../../services/authService';

const Settings = () => {
    const [userData, setUserData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        phone: '',
        company: '',
        position: '',
        notifications: true,
        twoFactor: true,
        language: 'es',
        logoUrl: ''
    });

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [success, setSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showPassword, setShowPassword] = useState({
        current: false,
        new: false,
        confirm: false
    });

    // Cargar datos del usuario al montar el componente
    useEffect(() => {
        loadUserData();
    }, []);

    const loadUserData = async() => {
        try {
            setInitialLoading(true);

            // Primero intentar cargar desde localStorage
            const storedUser = authService.getCurrentUser();
            if (storedUser) {
                setUserData({
                    firstName: storedUser.nombre || '',
                    lastName: storedUser.apellido || '',
                    email: storedUser.email || '',
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: '',
                    phone: storedUser.telefono || '',
                    company: storedUser.nombre_empresa || '',
                    position: storedUser.posicion || '',
                    notifications: true,
                    twoFactor: true,
                    language: 'es',
                    logoUrl: storedUser.logo_empresa || 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/5c/Banco_Bilbao_Vizcaya_Argentaria_logo.svg/1200px-Banco_Bilbao_Vizcaya_Argentaria_logo.svg.png'
                });
            }

            // Luego obtener datos actualizados del servidor
            const result = await userService.getCurrentUser();

            if (result.success && result.user) {
                setUserData(prev => ({
                    ...prev,
                    firstName: result.user.nombre || prev.firstName,
                    lastName: result.user.apellido || prev.lastName,
                    email: result.user.email || prev.email,
                    phone: result.user.telefono || prev.phone,
                    company: result.user.nombre_empresa || prev.company,
                    position: result.user.posicion || prev.position,
                    logoUrl: result.user.logo_empresa || prev.logoUrl
                }));
            } else if (result.error) {
                setErrorMessage(`Error al cargar datos: ${result.error}`);
            }
        } catch (error) {
            console.error('Error loading user data:', error);
            setErrorMessage('Error al cargar datos del usuario');
        } finally {
            setInitialLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setUserData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: '' }));
        }
        if (success) setSuccess(false);
        if (errorMessage) setErrorMessage('');
    };

    const validateForm = () => {
        const newErrors = {};

        // Validaciones básicas
        if (!userData.firstName.trim()) newErrors.firstName = 'Nombre requerido';
        if (!userData.lastName.trim()) newErrors.lastName = 'Apellido requerido';

        if (!userData.email.trim()) {
            newErrors.email = 'Email requerido';
        } else if (!/\S+@\S+\.\S+/.test(userData.email)) {
            newErrors.email = 'Email inválido';
        }

        // Validaciones de contraseña (solo si se está cambiando)
        const isChangingPassword = userData.newPassword || userData.confirmPassword;

        if (isChangingPassword) {
            if (!userData.currentPassword) {
                newErrors.currentPassword = 'La contraseña actual es requerida';
            }

            if (userData.newPassword.length < 8) {
                newErrors.newPassword = 'La contraseña debe tener al menos 8 caracteres';
            } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(userData.newPassword)) {
                newErrors.newPassword = 'Debe incluir mayúscula, minúscula y número';
            } else if (!/(?=.*[!@#$%^&*(),.?":{}|<>])/.test(userData.newPassword)) {
                newErrors.newPassword = 'Debe incluir al menos un carácter especial';
            }

            if (userData.newPassword !== userData.confirmPassword) {
                newErrors.confirmPassword = 'Las contraseñas no coinciden';
            }
        }

        return newErrors;
    };

    const handleSave = async(e) => {
        e.preventDefault();

        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);
        setErrors({});
        setErrorMessage('');

        try {
            const result = await userService.updateUser(userData, userData.currentPassword);

            if (result.success) {
                setSuccess(true);
                setErrorMessage('');

                // Limpiar campos de contraseña
                setUserData(prev => ({
                    ...prev,
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                }));

                // Actualizar datos en localStorage
                if (result.user) {
                    localStorage.setItem('user', JSON.stringify(result.user));
                }

                // Ocultar mensaje de éxito después de 3 segundos
                setTimeout(() => setSuccess(false), 3000);
            } else {
                setErrorMessage(result.error || 'Error al actualizar datos');
            }
        } catch (error) {
            console.error('Error saving user data:', error);
            setErrorMessage('Error al guardar los cambios. Por favor, inténtalo de nuevo.');
        } finally {
            setLoading(false);
        }
    };

    const handleLogoUpload = async(e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validaciones básicas del lado del cliente
        if (file.size > 2 * 1024 * 1024) {
            setErrorMessage('El archivo debe ser menor a 2MB');
            return;
        }

        if (!file.type.startsWith('image/')) {
            setErrorMessage('El archivo debe ser una imagen');
            return;
        }

        // Mostrar preview inmediato
        const reader = new FileReader();
        reader.onloadend = () => {
            setUserData(prev => ({...prev, logoUrl: reader.result }));
        };
        reader.readAsDataURL(file);

        // Subir archivo al servidor
        setLoading(true);
        setErrorMessage('');

        try {
            const result = await userService.uploadLogo(file);

            if (result.success) {
                setSuccess(true);
                setErrorMessage('');

                // Actualizar datos del usuario
                if (result.user) {
                    setUserData(prev => ({
                        ...prev,
                        logoUrl: result.user.logo_empresa || reader.result
                    }));

                    // Actualizar localStorage
                    localStorage.setItem('user', JSON.stringify(result.user));
                }

                // Ocultar mensaje de éxito después de 3 segundos
                setTimeout(() => setSuccess(false), 3000);
            } else {
                setErrorMessage(result.error || 'Error al subir logo');

                // Revertir preview si falla
                const storedUser = authService.getCurrentUser();
                if (storedUser && storedUser.logo_empresa) {
                    setUserData(prev => ({
                        ...prev,
                        logoUrl: storedUser.logo_empresa
                    }));
                }
            }
        } catch (error) {
            console.error('Error uploading logo:', error);
            setErrorMessage('Error al subir el logo. Por favor, inténtalo de nuevo.');

            // Revertir preview
            const storedUser = authService.getCurrentUser();
            if (storedUser && storedUser.logo_empresa) {
                setUserData(prev => ({
                    ...prev,
                    logoUrl: storedUser.logo_empresa
                }));
            }
        } finally {
            setLoading(false);
        }
    };

    const togglePasswordVisibility = (field) => {
        setShowPassword(prev => ({...prev, [field]: !prev[field] }));
    };

    const handleLogout = () => {
        authService.logout();
    };

    const FieldComponent = ({ field }) => {
        const value = userData[field.name];
        const error = errors[field.name];
        const Icon = field.icon;
        const isDisabled = field.disabled;

        if (field.type === 'switch') {
            return ( <
                    div className = "flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-gray-50 rounded-xl hover:shadow-sm transition-all" >
                    <
                    div className = "flex-1" >
                    <
                    span className = "text-sm font-semibold text-gray-800 block" > { field.label } < /span> {
                    field.description && ( <
                        p className = "text-xs text-gray-500 mt-0.5" > { field.description } < /p>
                    )
                } <
                /div> <
            div className = { `relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full transition-all duration-300 ${value ? 'bg-blue-600' : 'bg-gray-300'}` } >
                <
                input type = "checkbox"
            checked = { value }
            onChange = {
                (e) => handleInputChange({ target: { name: field.name, type: 'checkbox', checked: e.target.checked } })
            }
            className = "sr-only" /
                >
                <
                span className = { `absolute top-1 left-1 h-5 w-5 transform rounded-full bg-white shadow-lg transition-all duration-300 ${value ? 'translate-x-5' : ''}` }
            /> < /
            div > <
                /div>
        );
    }

    if (field.type === 'select') {
        return ( <
            div className = "space-y-2" >
            <
            label className = "text-sm font-semibold text-gray-800 flex items-center" > { Icon && < Icon className = "w-4 h-4 mr-2 text-blue-600" / > } { field.label } <
            /label> <
            div className = "relative group" >
            <
            select name = { field.name }
            value = { value }
            onChange = { handleInputChange }
            disabled = { isDisabled }
            className = { `w-full px-4 py-3 pl-10 bg-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all group-hover:border-gray-400 appearance-none cursor-pointer ${isDisabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''
                            }` } > {
                field.options.map(option => ( <
                    option key = { option.value }
                    value = { option.value } > { option.label } <
                    /option>
                ))
            } <
            /select> {
            Icon && < Icon className = { `absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${isDisabled ? 'text-gray-400' : 'text-gray-500 group-hover:text-blue-600'
                                  } transition-colors` }
            />} <
            div className = "absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" >
            <
            svg className = "w-4 h-4 text-gray-400"
            fill = "none"
            stroke = "currentColor"
            viewBox = "0 0 24 24" >
            <
            path strokeLinecap = "round"
            strokeLinejoin = "round"
            strokeWidth = "2"
            d = "M19 9l-7 7-7-7" / >
            <
            /svg> < /
            div > <
            /div> < /
            div >
        );
    }

    const isPasswordField = field.type === 'password';
    const showPwd = showPassword[field.name === 'currentPassword' ? 'current' : field.name === 'newPassword' ? 'new' : 'confirm'];

    return ( <
            div className = "space-y-2" >
            <
            label className = "text-sm font-semibold text-gray-800 flex items-center" > { Icon && < Icon className = "w-4 h-4 mr-2 text-blue-600" / > } { field.label } {
                field.required && < span className = "text-red-500 ml-1" > * < /span>} {
                isDisabled && ( <
                    span className = "ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded" >
                    Solo lectura <
                    /span>
                )
            } <
            /label> {
            field.note && ( <
                p className = "text-xs text-gray-500 italic bg-blue-50 px-2 py-1 rounded" > { field.note } < /p>
            )
        } <
        div className = "relative group" >
        <
        input
    type = { isPasswordField && showPwd ? 'text' : field.type }
    name = { field.name }
    value = { value }
    onChange = { handleInputChange }
    placeholder = { field.placeholder }
    disabled = { isDisabled }
    className = { `w-full px-4 py-3 pl-10 pr-10 bg-white border rounded-xl focus:outline-none focus:ring-2 transition-all ${error
                                                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                                      : isDisabled
                                                        ? 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed'
                                                        : 'border-gray-300 focus:ring-blue-500 focus:border-transparent group-hover:border-gray-400'
                                                      }` }
    /> {
    Icon && ( <
        Icon className = { `absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 transition-colors ${error ? 'text-red-400' :
                                                        isDisabled ? 'text-gray-400' : 'text-gray-500 group-hover:text-blue-600'
                                                        }` }
        />
    )
}

{
    isPasswordField && value && !isDisabled && ( <
        button type = "button"
        onClick = {
            () => togglePasswordVisibility(field.name === 'currentPassword' ? 'current' : field.name === 'newPassword' ? 'new' : 'confirm')
        }
        className = "absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-blue-600 transition-colors" > { showPwd ? < EyeOff className = "w-4 h-4" / > : < Eye className = "w-4 h-4" / > } <
        /button>
    )
} <
/div> {
error && ( <
p className = "text-red-500 text-sm flex items-center animate-pulse" >
<
AlertCircle className = "w-4 h-4 mr-1 flex-shrink-0" / > { error } <
/p>
)
} <
/div>
);
};

const settingSections = [{
        title: 'Perfil',
        icon: User,
        description: 'Información personal del usuario',
        color: 'from-blue-100 to-blue-50',
        fields: [
            { name: 'firstName', label: 'Nombre', type: 'text', placeholder: 'Tu nombre', icon: User, required: true },
            { name: 'lastName', label: 'Apellido', type: 'text', placeholder: 'Tu apellido', icon: User, required: true },
            { name: 'email', label: 'Correo Electrónico', type: 'email', placeholder: 'correo@bbva.com', icon: Mail, required: true },
            { name: 'phone', label: 'Teléfono', type: 'tel', placeholder: '+51 999 888 777', icon: Phone }
        ]
    },
    {
        title: 'Empresa',
        icon: Building,
        description: 'Información corporativa',
        color: 'from-indigo-100 to-indigo-50',
        fields: [{
                name: 'company',
                label: 'Empresa',
                type: 'text',
                placeholder: 'Nombre de la empresa',
                icon: Building,
                disabled: true
            },
            {
                name: 'position',
                label: 'Cargo',
                type: 'text',
                placeholder: 'Tu posición en la empresa',
                icon: User,
                disabled: true
            }
        ]
    },
    {
        title: 'Seguridad',
        icon: Shield,
        description: 'Protección de tu cuenta',
        color: 'from-emerald-100 to-emerald-50',
        fields: [
            { name: 'currentPassword', label: 'Contraseña Actual', type: 'password', placeholder: '••••••••', icon: Key, note: 'Necesaria para cambiar contraseña' },
            { name: 'newPassword', label: 'Nueva Contraseña', type: 'password', placeholder: '••••••••', icon: Lock, note: 'Mínimo 8 caracteres, con mayúsculas, minúsculas, números y caracteres especiales' },
            { name: 'confirmPassword', label: 'Confirmar Contraseña', type: 'password', placeholder: '••••••••', icon: Lock },
        ]
    }
];

// Mostrar loading inicial
if (initialLoading) {
    return ( <
        div className = "min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4 md:p-6 flex items-center justify-center" >
        <
        div className = "text-center" >
        <
        div className = "w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" > < /div> <
        p className = "text-gray-600" > Cargando datos del usuario... < /p> < /
        div > <
        /div>
    );
}

return ( <
    div className = "min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4 md:p-6" >
    <
    div className = "max-w-7xl mx-auto" > { /* Header */ } <
    div className = "mb-6 md:mb-8" >
    <
    div className = "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4" >
    <
    div >
    <
    h1 className = "text-2xl md:text-3xl font-bold text-gray-900 mb-2" > Configuración de Cuenta < /h1> <
    p className = "text-gray-600 text-sm md:text-base" > { userData.company ? `Usuario de ${userData.company}` : 'Gestión de perfil de usuario' } <
    /p> < /
    div >

    <
    div className = "flex items-center space-x-3" > {
        success && ( <
            div className = "animate-slideDown p-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg shadow-lg flex items-center" >
            <
            CheckCircle className = "w-5 h-5 mr-2" / >
            <
            span className = "font-medium" > ¡Configuración guardada! < /span> < /
            div >
        )
    }

    <
    button onClick = { handleLogout }
    className = "px-4 py-2 border border-red-300 text-red-600 rounded-xl hover:bg-red-50 transition-all font-medium flex items-center" >
    <
    LogOut className = "w-4 h-4 mr-2" / >
    Cerrar Sesión <
    /button> < /
    div > <
    /div>

    { /* Mensajes de error */ } {
        errorMessage && ( <
            div className = "animate-slideDown p-3 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-lg shadow-lg flex items-center mb-4" >
            <
            AlertCircle className = "w-5 h-5 mr-2 flex-shrink-0" / >
            <
            span className = "font-medium" > { errorMessage } < /span> < /
            div >
        )
    } <
    /div>

    <
    form onSubmit = { handleSave }
    className = "space-y-6" >
    <
    div className = "grid grid-cols-1 lg:grid-cols-3 gap-6" > { /* Columna izquierda - Logo y acciones */ } <
    div className = "lg:col-span-1 space-y-6" > { /* Card Logo */ } <
    div className = "bg-white rounded-2xl shadow-sm border border-gray-200 p-5 transition-all hover:shadow-md" >
    <
    div className = "flex items-center space-x-3 mb-5" >
    <
    div className = "w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-md" >
    <
    Image className = "w-5 h-5 text-white" / >
    <
    /div> <
    div >
    <
    h3 className = "font-bold text-gray-900" > Logo del Perfil < /h3> <
    p className = "text-sm text-gray-500" > Personaliza tu identidad < /p> < /
    div > <
    /div>

    <
    div className = "flex flex-col items-center" >
    <
    div className = "relative w-full max-w-xs mb-4" >
    <
    div className = "w-full aspect-square bg-gradient-to-br from-blue-50 to-gray-100 rounded-xl flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300 hover:border-blue-400 transition-colors" > {
        userData.logoUrl ? ( <
            img src = { userData.logoUrl }
            alt = "Logo"
            className = "w-full h-full object-contain p-4"
            onError = {
                (e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/300x300?text=Logo+Empresa';
                }
            }
            />
        ) : ( <
            div className = "text-center p-4" >
            <
            Image className = "w-12 h-12 text-gray-400 mx-auto mb-2" / >
            <
            p className = "text-sm text-gray-500" > Sin logo < /p> < /
            div >
        )
    } <
    /div> < /
    div > <
    /div> < /
    div >

    { /* Card Resumen */ } <
    div className = "bg-gradient-to-br from-blue-900 to-blue-800 rounded-2xl p-5 text-white shadow-lg" >
    <
    h3 className = "font-bold text-lg mb-4" > Resumen del Perfil < /h3> <
    div className = "space-y-3" >
    <
    div className = "flex items-center justify-between p-3 bg-white/10 rounded-lg backdrop-blur-sm" >
    <
    span className = "text-sm opacity-90" > Nombre completo < /span> <
    span className = "font-semibold" > { userData.firstName } { userData.lastName } < /span> < /
    div > <
    div className = "flex items-center justify-between p-3 bg-white/10 rounded-lg backdrop-blur-sm" >
    <
    span className = "text-sm opacity-90" > Correo < /span> <
    span className = "font-semibold truncate ml-2" > { userData.email } < /span> < /
    div > {
        userData.company && ( <
            div className = "flex items-center justify-between p-3 bg-white/10 rounded-lg backdrop-blur-sm" >
            <
            span className = "text-sm opacity-90" > Empresa < /span> <
            span className = "font-semibold" > { userData.company } < /span> < /
            div >
        )
    } {
        userData.position && ( <
            div className = "flex items-center justify-between p-3 bg-white/10 rounded-lg backdrop-blur-sm" >
            <
            span className = "text-sm opacity-90" > Cargo < /span> <
            span className = "font-semibold" > { userData.position } < /span> < /
            div >
        )
    } <
    /div> < /
    div > <
    /div>

    { /* Columna derecha - Configuración */ } <
    div className = "lg:col-span-2 space-y-6" >
    <
    div className = "grid grid-cols-1 md:grid-cols-2 gap-6" > {
        settingSections.map((section) => ( <
            div key = { section.title }
            className = { `bg-white rounded-2xl shadow-sm border border-gray-200 p-5 transition-all hover:shadow-md ${section.title === 'Seguridad' ? 'md:col-span-2' : ''}` } >
            <
            div className = "flex items-center space-x-3 mb-4" >
            <
            div className = { `w-12 h-12 rounded-xl bg-gradient-to-br ${section.color} flex items-center justify-center` } >
            <
            section.icon className = "w-6 h-6 text-gray-800" / >
            <
            /div> <
            div className = "flex-1" >
            <
            h3 className = "font-bold text-gray-900" > { section.title } < /h3> <
            p className = "text-sm text-gray-500" > { section.description } < /p> < /
            div > <
            /div>

            <
            div className = "space-y-4" > {
                section.fields.map((field) => ( <
                    div key = { field.name } >
                    <
                    FieldComponent field = { field }
                    /> < /
                    div >
                ))
            } <
            /div> < /
            div >
        ))
    } <
    /div>

    { /* Card de Acciones */ } <
    div className = "bg-gradient-to-r from-white to-gray-50 rounded-2xl shadow-lg border border-gray-200 p-6" >
    <
    div className = "flex flex-col md:flex-row md:items-center justify-between gap-4" >
    <
    div className = "flex-1" >
    <
    h3 className = "font-bold text-gray-900 mb-1" > Guardar Cambios < /h3> <
    p className = "text-sm text-gray-600" >
    Los ajustes se aplicarán inmediatamente.Verifica toda la información antes de guardar. <
    /p> < /
    div >

    <
    div className = "flex flex-col sm:flex-row gap-3" >
    <
    button type = "button"
    onClick = {
        () => loadUserData()
    }
    disabled = { loading }
    className = "px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-medium hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed" >
    Cancelar <
    /button> <
    button type = "submit"
    disabled = { loading }
    className = "px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[140px]" > {
        loading ? ( <
            >
            <
            div className = "w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" > < /div>
            Guardando... <
            />
        ) : ( <
            >
            <
            Save className = "w-4 h-4 mr-2" / >
            Guardar Cambios <
            />
        )
    } <
    /button> < /
    div > <
    /div>

    { /* Requisitos de contraseña */ } <
    div className = "mt-4 pt-4 border-t border-gray-200" >
    <
    p className = "text-xs font-semibold text-gray-600 mb-2" > Requisitos de contraseña: < /p> <
    div className = "grid grid-cols-2 md:grid-cols-4 gap-2" >
    <
    div className = { `text-xs px-2 py-1 rounded ${userData.newPassword?.length >= 8 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}` } > •Mínimo 8 caracteres <
    /div> <
    div className = { `text-xs px-2 py-1 rounded ${/[a-z]/.test(userData.newPassword) ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}` } > •Una minúscula <
    /div> <
    div className = { `text-xs px-2 py-1 rounded ${/[A-Z]/.test(userData.newPassword) ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}` } > •Una mayúscula <
    /div> <
    div className = { `text-xs px-2 py-1 rounded ${/\d/.test(userData.newPassword) ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}` } > •Un número <
    /div> <
    div className = { `text-xs px-2 py-1 rounded col-span-2 ${/[!@#$%^&*(),.?":{}|<>]/.test(userData.newPassword) ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}` } > •Un carácter especial(!@# $ % ^ & * ...) <
    /div> < /
    div > <
    /div> < /
    div > <
    /div> < /
    div > <
    /form> < /
    div > <
    /div>
);
};

export default Settings;